import { AxiosInstance } from 'axios'
import { IJsonApiSerializer } from '../JsonApiSerializer'
import { StateCode } from 'static/stateCodes'
import {
  IGuideFilterOptions,
  IGuideTourSkillBulkAssignmentPreview,
  ITourGuideByAssignmentsParam,
} from 'static/interfaces'
import { formatGuideFilterOptionsForAPI } from 'utils/guide'

class AgentService {
  public httpClient: AxiosInstance
  public serializer: IJsonApiSerializer

  constructor(httpClient: AxiosInstance, serializer: IJsonApiSerializer) {
    this.httpClient = httpClient
    this.serializer = serializer
  }

  public getDataFromJson(JsonData) {
    const data = this.serializer.parseResourceData(JsonData, JsonData.data)
    const pagination = { ...JsonData.meta }

    return { data, pagination }
  }

  public async createSkills(agent_skill) {
    const { data } = await this.httpClient.post('/agent/api/agent_skills', {
      agent_skill,
    })
    const createdSkills = this.serializer.parseResourceData(data.agent_skill, data.agent_skill.data)

    return { createdSkills, flush: data.flush }
  }
  public async updateSkills(agent_skill) {
    const { data } = await this.httpClient.patch(`/agent/api/agent_skills/${agent_skill.id}`, {
      agent_skill,
    })
    const updatedSkills = this.serializer.parseResourceData(data.agent_skill, data.agent_skill.data)

    return { updatedSkills, flush: data.flush }
  }
  public async deleteSkills(id) {
    return this.httpClient.delete(`/agent/api/agent_skills/${id}`)
  }

  public async createTourSkills(tourSkills) {
    const { data } = await this.httpClient.post('/agent/api/agent_tour_skills', {
      agent_tour_skill: tourSkills,
    })
    const createdTourSkills = this.serializer.parseResourceData(
      data.agent_tour_skill,
      data.agent_tour_skill.data
    )

    return { createdTourSkills, flush: data.flush }
  }

  public async updateTourSkills(tourSkills) {
    const { data } = await this.httpClient.patch(`/agent/api/agent_tour_skills/${tourSkills.id}`, {
      agent_tour_skill: tourSkills,
    })
    const updatedTourSkills = this.serializer.parseResourceData(
      data.agent_tour_skill,
      data.agent_tour_skill.data
    )

    return { updatedTourSkills, flush: data.flush }
  }

  public async deleteTourSkills(id) {
    return this.httpClient.delete(`/agent/api/agent_tour_skills/${id}`)
  }

  public async previewGuideTourSkillBulkAssignment(params: {
    agent_tour_skill_id: number
    identifiers: string
  }): Promise<IGuideTourSkillBulkAssignmentPreview> {
    const { data } = await this.httpClient.post(
      '/agent/api/guide_tour_skill_bulk_assignments/preview',
      params
    )
    return data
  }

  public async createGuideTourSkillBulkAssignment(params: {
    agent_tour_skill_id: number
    guide_ids: number[]
    level: number
  }) {
    const { data } = await this.httpClient.post(
      '/agent/api/guide_tour_skill_bulk_assignments',
      params
    )
    return data
  }

  public async createRangeTag(range_tag) {
    const { data } = await this.httpClient.post('/agent/api/range_tags', {
      range_tag,
    })
    const createdRangeTags = this.serializer.parseResourceData(data.range_tag, data.range_tag.data)

    return { createdRangeTags, flush: data.flush }
  }
  public async updateRangeTag(range_tag) {
    const { data } = await this.httpClient.patch(`/agent/api/range_tags/${range_tag.id}`, {
      range_tag,
    })
    const updatedRangeTags = this.serializer.parseResourceData(data.range_tag, data.range_tag.data)

    return { updatedRangeTags, flush: data.flush }
  }
  public async deleteRangeTag(id) {
    return this.httpClient.delete(`/agent/api/range_tags/${id}`)
  }

  public async createTask(task: { name: string; label: string; position: number }) {
    const { data } = await this.httpClient.post('/agent/api/tasks', {
      task,
    })
    const createdTask = this.serializer.parseResourceData(data.task, data.task.data)

    return { createdTask, flush: data.flush }
  }

  public async updateTask(
    id,
    task: {
      name: string
      label: string
      position: number
    }
  ) {
    const { data } = await this.httpClient.patch(`/agent/api/tasks/${id}`, {
      task,
    })
    const updatedTask = this.serializer.parseResourceData(data.task, data.task.data)

    return { updatedTask, flush: data.flush }
  }

  public async deleteTask(id) {
    return this.httpClient.delete(`/agent/api/tasks/${id}`)
  }

  // 打診
  public async createSoundOutTemplate(sound_out_template) {
    const { data } = await this.httpClient.post(`/agent/api/sound_out_templates`, {
      sound_out_template,
    })
    const createdTemplate = this.serializer.parseResourceData(
      data.sound_out_template,
      data.sound_out_template.data
    )

    return { createdTemplate, flush: data.flush }
  }

  public async updateSoundOutTemplate(id, sound_out_template) {
    const { data } = await this.httpClient.put(`/agent/api/sound_out_templates/${id}`, {
      sound_out_template,
    })
    const updatedSoundOutTemplate = this.serializer.parseResourceData(
      data.sound_out_template,
      data.sound_out_template.data
    )

    return { updatedSoundOutTemplate, flush: data.flush }
  }

  public async deleteSoundOutTemplate(id) {
    const { data } = await this.httpClient.delete(`/agent/api/sound_out_templates/${id}`)

    return { flush: data.flush }
  }

  // ツアーメモテンプレ
  public async createTourMemoTemplate(tour_memo_template) {
    const { data } = await this.httpClient.post(`/agent/api/tour_memo_templates`, {
      tour_memo_template,
    })
    const createdTemplate = this.serializer.parseResourceData(
      data.tour_memo_template,
      data.tour_memo_template.data
    )

    return { createdTemplate, flush: data.flush }
  }

  public async updateTourMemoTemplate(id, tour_memo_template) {
    const { data } = await this.httpClient.put(`/agent/api/tour_memo_templates/${id}`, {
      tour_memo_template,
    })
    const updatedTemplate = this.serializer.parseResourceData(
      data.tour_memo_template,
      data.tour_memo_template.data
    )

    return { updatedTemplate, flush: data.flush }
  }

  public async deleteTourMemoTemplate(id) {
    const { data } = await this.httpClient.delete(`/agent/api/tour_memo_templates/${id}`)

    return { flush: data.flush }
  }

  // 正式依頼
  public async createFormalRequestTemplate(formal_request_template) {
    const { data } = await this.httpClient.post(`/agent/api/formal_request_templates`, {
      formal_request_template,
    })
    const createdTemplate = this.serializer.parseResourceData(
      data.formal_request_template,
      data.formal_request_template.data
    )

    return { createdTemplate, flush: data.flush }
  }

  public async updateFormalRequestTemplate(id, formal_request_template) {
    const { data } = await this.httpClient.put(`/agent/api/formal_request_templates/${id}`, {
      formal_request_template,
    })
    const updatedFormalRequestTemplate = this.serializer.parseResourceData(
      data.formal_request_template,
      data.formal_request_template.data
    )

    return { updatedFormalRequestTemplate, flush: data.flush }
  }

  public async deleteFormalRequestTemplate(id) {
    const { data } = await this.httpClient.delete(`/agent/api/formal_request_templates/${id}`)

    return { flush: data.flush }
  }

  // つなぐメール
  public async createConnectionMailTemplate(connection_mail_template) {
    const { data } = await this.httpClient.post(`/agent/api/connection_mail_templates`, {
      connection_mail_template,
    })
    const createdTemplate = data.connection_mail_template
      ? this.serializer.parseResourceData(
          data.connection_mail_template,
          data.connection_mail_template.data
        )
      : null

    return { createdTemplate, flush: data.flush }
  }

  public async updateConnectionMailTemplate(id, connection_mail_template) {
    const { data } = await this.httpClient.put(`/agent/api/connection_mail_templates/${id}`, {
      connection_mail_template,
    })

    const updatedConnectionMailTemplate = this.serializer.parseResourceData(
      data.connection_mail_template,
      data.connection_mail_template.data
    )

    return { updatedConnectionMailTemplate, flush: data.flush }
  }

  public async deleteConnectionMailTemplate(id) {
    const res = await this.httpClient.delete(`/agent/api/connection_mail_templates/${id}`)
    return { flush: res.data?.flush }
  }

  // サンクスメール
  public async createMailTemplate(mail_template) {
    const { data } = await this.httpClient.post(`/agent/api/mail_templates`, { mail_template })
    const createdMailTemplate = this.serializer.parseResourceData(
      data.mail_template,
      data.mail_template.data
    )

    return { createdMailTemplate, flush: data.flush }
  }

  public async updateMailTemplate(id, mail_template) {
    const { data } = await this.httpClient.put(`/agent/api/mail_templates/${id}`, { mail_template })
    const updatedMailTemplate = this.serializer.parseResourceData(
      data.mail_template,
      data.mail_template.data
    )

    return { updatedMailTemplate, flush: data.flush }
  }

  public async deleteMailTemplate(id) {
    const { data } = await this.httpClient.delete(`/agent/api/mail_templates/${id}`)

    return { flush: data.flush }
  }

  public async updateReservation(reservation: any, isReservation: boolean) {
    const { id } = reservation
    const param = isReservation
      ? { reservation }
      : {
          direct_reservation: { ...reservation },
        }

    const { data } = await this.httpClient.patch(`/agent/api/reservations/${id}`, param)

    const updatedReservation = this.serializer.parseResourceData(
      data.reservation,
      data.reservation.data
    )

    return { updatedReservation, flush: data.flush }
  }

  public async updateGuestEmails(
    ota_reservation_id: number,
    guest_emails: Array<{
      id: number | null
      email: string
    }>
  ) {
    const {
      data,
    } = await this.httpClient.patch(
      `/agent/api/ota_reservations/${ota_reservation_id}/guest_emails`,
      { guest_emails }
    )
    const updatedGuestEmails = this.serializer.parseResourceData(
      data.guest_emails,
      data.guest_emails.data
    )
    return { updatedGuestEmails, flush: data.flush }
  }

  public async createAgentRank(agent_rank) {
    const { data } = await this.httpClient.post('/agent/api/agent_ranks', { agent_rank })
    return { flush: data.flush }
  }

  public async updateAgentRank(agent_rank) {
    const { data } = await this.httpClient.patch(`/agent/api/agent_ranks/${agent_rank.id}`, {
      agent_rank,
    })
    return { flush: data.flush }
  }

  public async deleteAgentRank(id) {
    const { data } = await this.httpClient.delete(`/agent/api/agent_ranks/${id}`)
    return { flush: data.flush }
  }

  public async downloadDbPayments(guides, month, share, issue_date) {
    const { data } = await this.httpClient.post(`/agent/api/payments/download`, {
      guides,
      month,
      share,
      issue_date,
    })
    return { flush: data.flush }
  }

  public async downloadSpreadSheetPayments(guides, month, share, issue_date) {
    const { data } = await this.httpClient.post(`/agent/api/payments/spreadsheet_download`, {
      guides,
      month,
      share,
      issue_date,
    })
    return { flush: data.flush }
  }

  public async postFormalRequests(formData) {
    const { data, status } = await this.httpClient.post(`/agent/api/formal_requests`, formData)

    return { tourId: data.tour_id, flush: data.flush, status }
  }

  public async postSoundOuts(formData) {
    const { data, status } = await this.httpClient.post(`/agent/api/sound_outs`, formData)

    return { tourId: data.tour_id, flush: data.flush, status }
  }

  public async cancelSoundOutAll(tour_id, send_notification) {
    await this.httpClient.put(`/agent/api/sound_outs/all_cancel_in_tour`, {
      tour_id,
      send_notification,
    })
  }

  public async cancelSoundOut(tourId: number, tourGuideId: number, sendNotification: boolean) {
    await this.httpClient.put(`/agent/api/sound_outs/${tourGuideId}/cancel`, {
      tour_id: tourId,
      send_notification: sendNotification,
    })
  }

  public async tourGuideCancel(id, params) {
    await this.httpClient.put(`/agent/api/formal_requests/${id}/cancel`, params)
  }

  public async getPostRequestTemplates(post_id: number) {
    const { data } = await this.httpClient.get(
      `/agent/api/weekly/posts/${post_id}/request_templates`
    )
    const meeting_point_name: string | null = data.meeting_point_name ?? null
    const sound_out_templates = this.serializer.parseResourceData(
      data.sound_out_templates,
      data.sound_out_templates.data
    )
    const formal_request_templates = this.serializer.parseResourceData(
      data.formal_request_templates,
      data.formal_request_templates.data
    )
    const agent_ranks = this.serializer.parseResourceData(data.agent_ranks, data.agent_ranks.data)
    const post_rank_fees = this.serializer.parseResourceData(
      data.post_rank_fees,
      data.post_rank_fees.data
    )

    // ツアーの支払設定の未設定項目('guide_fee' | 'post_cost')。正式依頼モーダルの警告表示に使う
    const missing_payment_settings: string[] = data.missing_payment_settings || []
    // デフォルトテンプレ判定の対象post id(優先順: 自post > family owner > DMCツアーのowner post)
    const target_post_ids: number[] = data.target_post_ids || []

    return {
      meeting_point_name,
      target_post_ids,
      sound_out_templates,
      formal_request_templates,
      agent_ranks,
      post_rank_fees,
      missing_payment_settings,
    }
  }

  public async getWeeklyTour(tour_id: number) {
    const { data } = await this.httpClient.get(`/agent/api/tours/${tour_id}/weekly_tour`)
    const tour = this.serializer.parseResourceData(data.tour, data.tour.data)

    return { tour, flush: data.flush }
  }

  public async updateTourPaymentExclusion(tour_id, params) {
    const { data } = await this.httpClient.patch(
      `/agent/api/tours/${tour_id}/payment_exclusion`,
      params
    )
    const tour = this.serializer.parseResourceData(data.tour, data.tour.data)

    return { tour, flush: data.flush }
  }

  public async updateTourMemo(tour_id, params) {
    const { data } = await this.httpClient.patch(`/agent/api/tours/${tour_id}/memo`, {
      tour: params,
    })
    const tour = this.serializer.parseResourceData(data.tour, data.tour.data)

    return { tour, flush: data.flush }
  }

  public async updateTourGroupMemo(tour_group_id, params) {
    const { data } = await this.httpClient.patch(`/agent/api/tour_groups/${tour_group_id}/memo`, {
      tour_group: params,
    })
    const tour_group = this.serializer.parseResourceData(data.tour_group, data.tour_group.data)

    return { tour_group, flush: data.flush }
  }

  public async getConnectionMailTemplates() {
    const { data } = await this.httpClient.get(`/agent/api/connection_mail_templates`)
    const connection_mail_templates = this.serializer.parseResourceData(
      data.connection_mail_templates,
      data.connection_mail_templates.data
    )
    return { connection_mail_templates }
  }

  public async getConnectMessagePreview(id: number, mail_template_id: number | null) {
    const { data } = await this.httpClient.get(
      `/agent/api/ota_reservations/${id}/connect_message_preview?mail_template_id=${mail_template_id ??
        ''}`
    )
    return {
      messagePreview: {
        ...data.message_preview,
      },
    }
  }

  public async connectTourGroup(
    tour_group_id,
    params: { subject: string; body: string; send_mail: boolean }
  ) {
    const { data } = await this.httpClient.post(
      `/agent/api/tour_groups/${tour_group_id}/connect`,
      params
    )
    const tour_group = this.serializer.parseResourceData(data.tour_group, data.tour_group.data)

    return { tour_group, flush: data.flush }
  }

  public async createCalendarMemo(params) {
    const { data } = await this.httpClient.post(`/agent/api/calendar_memos`, {
      calendar_memo: params,
    })

    const updatedCalendarMemo = this.serializer.parseResourceData(
      data.calendar_memo,
      data.calendar_memo.data
    )

    return { updatedCalendarMemo, flush: data.flush }
  }

  public async updateCalendarMemo(calendar_memo_id, params) {
    const { data } = await this.httpClient.patch(`/agent/api/calendar_memos/${calendar_memo_id}`, {
      calendar_memo: params,
    })

    const updatedCalendarMemo = this.serializer.parseResourceData(
      data.calendar_memo,
      data.calendar_memo.data
    )

    return { updatedCalendarMemo, flush: data.flush }
  }

  public async deleteCalendarMemo(calendar_memo_id) {
    const { data } = await this.httpClient.delete(`/agent/api/calendar_memos/${calendar_memo_id}`)

    const updatedCalendarMemo = this.serializer.parseResourceData(
      data.calendar_memo,
      data.calendar_memo.data
    )

    return { updatedCalendarMemo, flush: data.flush }
  }

  public async setCalendarMemo(calendar_memo_id, params) {
    if (!calendar_memo_id) {
      return this.createCalendarMemo(params)
    }
    if (params.memo === '' && params.internal_memo === '') {
      return this.deleteCalendarMemo(calendar_memo_id)
    }

    return this.updateCalendarMemo(calendar_memo_id, params)
  }

  public async createGuide(params) {
    const { data } = await this.httpClient.post(`/agent/api/guides`, params)
    const createdUser = this.serializer.parseResourceData(data.guide, data.guide.data)

    return { user: createdUser, flush: data.flush }
  }

  public async updateGuide(guide_id, params) {
    const { data } = await this.httpClient.patch(`/agent/api/guides/${guide_id}`, params)
    const updatedUser = this.serializer.parseResourceData(data.guide, data.guide.data)

    return { user: updatedUser, flush: data.flush }
  }

  public async getGuides(params: IGuideFilterOptions, tour, page, with_image = true) {
    const { data } = await this.httpClient.get(
      `/agent/api/guides?page=${page}${with_image ? '&with_image=true' : ''}`,
      { params: formatGuideFilterOptionsForAPI(params, tour) }
    )
    const guides = this.serializer.parseResourceData(data.guides.users, data.guides.users.data)
    const pagination = data.guides.users.meta

    return { guides, pagination }
  }

  public async getTourGuidesByAssignments(params: ITourGuideByAssignmentsParam) {
    const { data } = await this.httpClient.get(`/agent/api/tour_guides/assignments`, {
      params,
    })
    const tourGuides = this.serializer.parseResourceData(data.tour_guides, data.tour_guides.data)

    return { tourGuides }
  }

  public async getAgentSkills() {
    const { data } = await this.httpClient.get(`/agent/api/agent_skills`)
    const agent_skills = this.serializer.parseResourceData(
      data.agent_skills,
      data.agent_skills.data
    )

    return { agent_skills }
  }

  public async getAgentTourSkills() {
    const { data } = await this.httpClient.get(`/agent/api/agent_tour_skills`)
    const agent_tour_skills = this.serializer.parseResourceData(
      data.agent_tour_skills,
      data.agent_tour_skills.data
    )

    return { agent_tour_skills }
  }

  public async getAgentRanks() {
    const { data } = await this.httpClient.get(`/agent/api/agent_ranks`)
    const agent_ranks = this.serializer.parseResourceData(data.agent_ranks, data.agent_ranks.data)

    return { agent_ranks }
  }

  public async updateRelatedPosts(post_id, related_post_ids) {
    const { data } = await this.httpClient.patch(`/agent/api/posts/${post_id}/related_posts`, {
      related_post_ids,
    })
    return { flush: data.flush }
  }

  public async createRelatedPosts(post_id, related_post_ids) {
    const { data } = await this.httpClient.post(`/agent/api/posts/${post_id}/related_posts`, {
      related_post_ids,
    })
    return { flush: data.flush }
  }

  public async updateMemberPosts(post_id, member_post_ids) {
    const { data } = await this.httpClient.patch(`/agent/api/posts/${post_id}/member_posts`, {
      post: { member_post_ids },
    })
    return { flush: data.flush }
  }

  public async createCancelPolicy(cancel_policy) {
    const { data } = await this.httpClient.post('/agent/api/cancel_policies', {
      cancel_policy,
    })
    const cancelPolicy = this.serializer.parseResourceData(
      data.cancel_policy,
      data.cancel_policy.data
    )
    return { cancelPolicy, flush: data.flush }
  }
  public async updateCancelPolicy(cancel_policy) {
    const { data } = await this.httpClient.patch(
      `/agent/api/cancel_policies/${cancel_policy.cancel_policy_attributes.id}`,
      {
        cancel_policy,
      }
    )
    const cancelPolicy = this.serializer.parseResourceData(
      data.cancel_policy,
      data.cancel_policy.data
    )
    return { cancelPolicy, flush: data.flush }
  }

  public async getMeetupPlaces(params) {
    const { data } = await this.httpClient.get(`/agent/api/meetup_places`, params)
    return { meetup_places: data.meetup_places }
  }

  public async createMeetupPlace(meetup_place: {
    area: StateCode
    sub_area: string
    name_ja: string
    name_en: string
    meetup_point: string
    meetup_point_url: string
  }) {
    const { data } = await this.httpClient.post('/agent/api/meetup_places', {
      meetup_place,
    })
    const createdMeetupPlace = this.serializer.parseResourceData(
      data.meetup_place,
      data.meetup_place.data
    )

    return { createdMeetupPlace, flush: data.flush }
  }

  public async updateMeetupPlace(
    id,
    meetup_place: {
      area: StateCode
      sub_area: string
      name_ja: string
      name_en: string
      meetup_point: string
      meetup_point_url: string
    }
  ) {
    const { data } = await this.httpClient.put(`/agent/api/meetup_places/${id}`, {
      meetup_place,
    })
    const updatedMeetupPlace = this.serializer.parseResourceData(
      data.meetup_place,
      data.meetup_place.data
    )

    return { updatedMeetupPlace, flush: data.flush }
  }

  public async deleteMeetupPlace(id) {
    const { data } = await this.httpClient.delete(`/agent/api/meetup_places/${id}`)

    return { flush: data.flush }
  }

  public async createPartnerVenue(partner_venue: {
    name: string
    slug: string
    login_id: string
    is_active: boolean
    is_guest_list_active: boolean
    password: string
    post_ids: number[]
  }) {
    const { data } = await this.httpClient.post('/agent/api/partner_venues', {
      partner_venue,
    })

    return { flush: data.flush }
  }

  public async updatePartnerVenue(
    id,
    partner_venue: {
      name: string
      slug: string
      login_id: string
      is_active: boolean
      is_guest_list_active: boolean
      password?: string
      post_ids: number[]
    }
  ) {
    const { data } = await this.httpClient.put(`/agent/api/partner_venues/${id}`, {
      partner_venue,
    })

    return { flush: data.flush }
  }

  public async getOtaReservations(params, page) {
    const { data } = await this.httpClient.get(`/agent/api/ota_reservations?page=${page}`, {
      params,
    })
    const ota_reservations = this.serializer.parseResourceData(
      data.ota_reservations,
      data.ota_reservations.data
    )
    const pagination = data.ota_reservations.meta

    return { ota_reservations, pagination }
  }

  public async getReviews(page?: number) {
    const { data } = await this.httpClient.get(`/agent/api/reviews`, {
      params: { page },
    })
    const reviews = this.serializer.parseResourceData(data.reviews, data.reviews.data)
    const pagination = data.reviews.meta

    return { reviews, pagination }
  }

  public async toggleReviewIsOpen(id: number) {
    const { data } = await this.httpClient.patch(`/agent/api/reviews/${id}/toggle_is_open`)
    const review = this.serializer.parseResourceData(data.review, data.review.data)

    return { review }
  }

  public async toggleReviewImageIsOpen(id: number) {
    const { data } = await this.httpClient.patch(`/agent/api/review_images/${id}/toggle_is_open`)
    return { reviewImage: data.review_image }
  }

  public async createGuideAnnouncement(guide_announcement) {
    const { data } = await this.httpClient.post(`/agent/api/guide_announcements`, {
      guide_announcement,
    })
    const createdGuideAnnouncement = this.serializer.parseResourceData(
      data.guide_announcement,
      data.guide_announcement.data
    )

    return { createdGuideAnnouncement, flush: data.flush }
  }

  public async updateGuideAnnouncement(id, guide_announcement) {
    const { data } = await this.httpClient.put(`/agent/api/guide_announcements/${id}`, {
      guide_announcement,
    })
    const updatedGuideAnnouncement = this.serializer.parseResourceData(
      data.guide_announcement,
      data.guide_announcement.data
    )

    return { updatedGuideAnnouncement, flush: data.flush }
  }

  public async deleteGuideAnnouncement(id) {
    const { data } = await this.httpClient.delete(`/agent/api/guide_announcements/${id}`)

    return { flush: data.flush }
  }

  // ガイド利用規約
  public async createTermsVersion(terms_version) {
    const { data } = await this.httpClient.post(`/agent/api/terms_versions`, {
      terms_version,
    })
    // 422 等のバリデーションエラー時は data が undefined または terms_version 未含のため、ガードして flush のみ返す
    if (!data?.terms_version) {
      return { createdTermsVersion: null, flush: data?.flush }
    }
    const createdTermsVersion = this.serializer.parseResourceData(
      data.terms_version,
      data.terms_version.data
    )

    return { createdTermsVersion, flush: data.flush }
  }

  public async updateTermsVersion(id, terms_version) {
    const { data } = await this.httpClient.put(`/agent/api/terms_versions/${id}`, {
      terms_version,
    })
    if (!data?.terms_version) {
      return { updatedTermsVersion: null, flush: data?.flush }
    }
    const updatedTermsVersion = this.serializer.parseResourceData(
      data.terms_version,
      data.terms_version.data
    )

    return { updatedTermsVersion, flush: data.flush }
  }

  public async deleteTermsVersion(id) {
    const { data } = await this.httpClient.delete(`/agent/api/terms_versions/${id}`)

    return { flush: data.flush }
  }

  // 通知テンプレート
  public async createGuideNotificationType(guide_notification_type) {
    const { data } = await this.httpClient.post(`/agent/api/guide_notification_types`, {
      guide_notification_type,
    })
    const createdGuideNotificationType = this.serializer.parseResourceData(
      data.guide_notification_type,
      data.guide_notification_type.data
    )

    return { createdGuideNotificationType, flush: data.flush }
  }

  public async updateGuideNotificationType(id, guide_notification_type) {
    const { data } = await this.httpClient.put(`/agent/api/guide_notification_types/${id}`, {
      guide_notification_type,
    })
    const updatedGuideNotificationType = this.serializer.parseResourceData(
      data.guide_notification_type,
      data.guide_notification_type.data
    )

    return { updatedGuideNotificationType, flush: data.flush }
  }

  public async deleteGuideNotificationType(id) {
    const { data } = await this.httpClient.delete(`/agent/api/guide_notification_types/${id}`)

    return { flush: data.flush }
  }
  // === エージェントユーザー管理 ===

  public async getAgentMembers() {
    const { data } = await this.httpClient.get('/agent/api/agent_members')
    return data
  }

  public async createAgentMember(agent_member) {
    const { data } = await this.httpClient.post('/agent/api/agent_members', { agent_member })
    const created = this.serializer.parseResourceData(data.agent_member, data.agent_member.data)
    return { agentMember: created, flush: data.flush }
  }

  public async updateAgentMember(agent_member) {
    const { data } = await this.httpClient.patch(`/agent/api/agent_members/${agent_member.id}`, {
      agent_member,
    })
    const updated = this.serializer.parseResourceData(data.agent_member, data.agent_member.data)
    return { agentMember: updated, flush: data.flush }
  }

  public async deleteAgentMember(id) {
    return this.httpClient.delete(`/agent/api/agent_members/${id}`)
  }

  public async updateAgentMemberPassword(params: {
    current_password: string
    password: string
    password_confirmation: string
  }) {
    const { data } = await this.httpClient.patch('/agent/api/agent_member_password', params)
    return { flush: data.flush }
  }

  // === エージェントロール管理 ===

  public async getAgentRoles() {
    const { data } = await this.httpClient.get('/agent/api/agent_roles')
    return data
  }

  public async createAgentRole(agent_role) {
    const { data } = await this.httpClient.post('/agent/api/agent_roles', { agent_role })
    const created = this.serializer.parseResourceData(data.agent_role, data.agent_role.data)
    return { agentRole: created, flush: data.flush }
  }

  public async updateAgentRole(agent_role) {
    const { data } = await this.httpClient.patch(`/agent/api/agent_roles/${agent_role.id}`, {
      agent_role,
    })
    const updated = this.serializer.parseResourceData(data.agent_role, data.agent_role.data)
    return { agentRole: updated, flush: data.flush }
  }

  public async deleteAgentRole(id) {
    return this.httpClient.delete(`/agent/api/agent_roles/${id}`)
  }

  // === 権限マトリクス ===

  public async getPermissionMatrix(agentRoleId) {
    const { data } = await this.httpClient.get('/agent/api/agent_role_permissions', {
      params: { agent_role_id: agentRoleId },
    })
    return data
  }

  public async getAllRolesPermissionMatrix() {
    const { data } = await this.httpClient.get('/agent/api/agent_role_permissions/matrix')
    return data
  }

  public async updateRolePermission(params) {
    const { data } = await this.httpClient.patch(
      `/agent/api/agent_role_permissions/${params.id || 0}`,
      {
        role_permission: params,
      }
    )
    return data
  }
}

export default AgentService
